import { useState, useEffect } from "react";
import { 
  X, User, Phone, Mail, MapPin, Calendar, Clock, Building2, 
  MessageSquare, Edit3, Save, RefreshCw, UserCheck, Building,
  FileText, Tag, Users, ExternalLink, Send, Bot, Sparkles, Check, AlertCircle,
  Trash2, AlertTriangle, ChevronDown
} from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Raisons d'annulation/suppression de RDV
const RAISONS_ANNULATION = [
  { id: "client_absent", label: "Client absent au RDV" },
  { id: "client_annule", label: "Annulé par le client" },
  { id: "client_reporte", label: "Report demandé par le client" },
  { id: "client_injoignable", label: "Client injoignable (NRP)" },
  { id: "client_refuse", label: "Client ne souhaite plus donner suite" },
  { id: "client_reflexion", label: "Client en réflexion" },
  { id: "doublon", label: "Doublon dans la base" },
  { id: "hors_zone", label: "Hors zone géographique" },
  { id: "erreur_saisie", label: "Erreur de saisie / RDV incorrect" },
  { id: "technicien_indispo", label: "Technicien indisponible" },
  { id: "reorganisation_planning", label: "Réorganisation du planning" },
  { id: "autre", label: "Autre raison" }
];

export default function RdvDetailModal({ rdv, onClose, onUpdate, onViewFullProfile, onDelete, currentUser }) {
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingComment, setEditingComment] = useState(false);
  const [commentaire, setCommentaire] = useState("");
  const [saving, setSaving] = useState(false);
  
  // État pour la suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteComment, setDeleteComment] = useState("");
  
  // Mode édition des infos client
  const [editingInfo, setEditingInfo] = useState(false);
  const [editForm, setEditForm] = useState({
    telephone: '',
    adresse: '',
    cp: '',
    ville: ''
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [savingInfo, setSavingInfo] = useState(false);
  
  // Nouvelle section IA
  const [iaQuestion, setIaQuestion] = useState("");
  const [iaResponse, setIaResponse] = useState(null);
  const [iaLoading, setIaLoading] = useState(false);
  
  // Fonction de suppression du RDV
  const handleDeleteRdv = async () => {
    if (!rdv?.rowNumber || !deleteReason) return;
    
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/api/calendrier/rdv/${rdv.id || rdv.rowNumber}/cancel`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rowNumber: rdv.rowNumber,
          type: rdv.type || 'R1',
          raison: deleteReason,
          commentaire: deleteComment,
          supprime_par: currentUser?.email || currentUser?.name || 'Utilisateur inconnu',
          client_nom: rdv?.client || clientDetails?.nom + ' ' + clientDetails?.prenom,
          date_rdv: rdv?.date
        })
      });
      
      if (response.ok) {
        // Notifier le parent pour rafraîchir le calendrier
        if (onDelete) {
          onDelete(rdv);
        }
        if (onUpdate) {
          onUpdate();
        }
        onClose();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail || 'Impossible de supprimer le RDV'}`);
      }
    } catch (e) {
      console.error("Erreur suppression RDV:", e);
      alert("Erreur lors de la suppression du RDV");
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
    setDeleteReason("");
    setDeleteComment("");
  };
  
  // Déterminer le responsable IA selon le type de RDV
  const getIaResponsable = () => {
    // Pour R1 -> Milan (ou le technicien), Pour R2 -> Architecte, Sinon -> San-ia
    if (rdv?.type === "R1") {
      return { 
        name: "Milan", 
        role: "Responsable R1 & Optimisation Tournées",
        avatar: "👷",
        color: "blue"
      };
    } else if (rdv?.type === "R2") {
      return { 
        name: "Léa", 
        role: "Directrice Commerciale",
        avatar: "💼",
        color: "purple"
      };
    }
    return { 
      name: "San-ia", 
      role: "Assistante IA Générale",
      avatar: "🤖",
      color: "amber"
    };
  };
  
  const iaResponsable = getIaResponsable();

  // Charger les détails complets du client
  useEffect(() => {
    const loadDetails = async () => {
      if (!rdv?.rowNumber) {
        setLoading(false);
        return;
      }
      
      try {
        // Charger les détails depuis le pipeline
        const resp = await authFetch(`${API}/pipeline/client/by-row/${encodeURIComponent(rdv.clientId || rdv.rowNumber)}`);
        if (resp.ok) {
          const data = await resp.json();
          setClientDetails(data);
          setCommentaire(data.commentaire_rdv || "");
          // Initialiser le formulaire d'édition avec les valeurs existantes
          setEditForm({
            telephone: data.telephones?.[0] || '',
            adresse: data.adresse || '',
            cp: data.code_postal || '',
            ville: data.ville || ''
          });
        }
      } catch (e) {
        console.error("Erreur chargement détails:", e);
      }
      setLoading(false);
    };
    
    loadDetails();
  }, [rdv]);
  
  // Recherche d'adresse avec Nominatim (OpenStreetMap)
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setLoadingAddress(true);
    try {
      const response = await authFetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=fr&format=json&addressdetails=1&limit=5`,
        { headers: { 'Accept-Language': 'fr' } }
      );
      const data = await response.json();
      
      const suggestions = data.map(item => ({
        display: item.display_name,
        adresse: item.address?.road || item.address?.pedestrian || item.address?.hamlet || '',
        numero: item.address?.house_number || '',
        cp: item.address?.postcode || '',
        ville: item.address?.city || item.address?.town || item.address?.village || item.address?.municipality || '',
        full: `${item.address?.house_number || ''} ${item.address?.road || item.address?.pedestrian || ''}`.trim()
      }));
      
      setAddressSuggestions(suggestions);
      setShowSuggestions(true);
    } catch (err) {
      console.error("Erreur recherche adresse:", err);
    }
    setLoadingAddress(false);
  };
  
  // Sélectionner une adresse suggérée
  const selectAddress = (suggestion) => {
    setEditForm(prev => ({
      ...prev,
      adresse: suggestion.full || suggestion.adresse,
      cp: suggestion.cp,
      ville: suggestion.ville
    }));
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };
  
  // Sauvegarder les modifications des infos client
  const saveClientInfo = async () => {
    if (!rdv?.rowNumber) return;
    
    setSavingInfo(true);
    try {
      // Sauvegarder via l'API
      const resp = await authFetch(`${API}/pipeline/client/row_${rdv.rowNumber}/update-fields`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          telephone: editForm.telephone,
          adresse: editForm.adresse,
          code_postal: editForm.cp,
          ville: editForm.ville
        })
      });
      
      if (resp.ok) {
        // Mettre à jour les détails locaux
        setClientDetails(prev => ({
          ...prev,
          telephones: [editForm.telephone],
          adresse: editForm.adresse,
          code_postal: editForm.cp,
          ville: editForm.ville
        }));
        setEditingInfo(false);
        if (onUpdate) onUpdate();
      }
    } catch (e) {
      console.error("Erreur sauvegarde infos:", e);
    }
    setSavingInfo(false);
  };
  
  // Vérifier si les infos sont complètes
  const hasCompleteInfo = () => {
    return (
      (clientDetails?.telephones?.[0] || editForm.telephone) &&
      (clientDetails?.adresse || editForm.adresse) &&
      (clientDetails?.ville || editForm.ville)
    );
  };

  // Sauvegarder le commentaire
  const saveCommentaire = async () => {
    if (!clientDetails?.client_id) return;
    
    setSaving(true);
    try {
      const resp = await authFetch(`${API}/pipeline/client/${encodeURIComponent(clientDetails.client_id)}/commentaire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ commentaire_rdv: commentaire })
      });
      
      if (resp.ok) {
        setEditingComment(false);
        if (onUpdate) onUpdate();
      }
    } catch (e) {
      console.error("Erreur sauvegarde:", e);
    }
    setSaving(false);
  };
  
  // Poser une question au responsable IA
  const askIaQuestion = async () => {
    if (!iaQuestion.trim() || !clientDetails) return;
    
    setIaLoading(true);
    try {
      // Construire le contexte client pour l'IA
      const clientContext = {
        nom: clientDetails.nom,
        prenom: clientDetails.prenom,
        adresse: clientDetails.adresse,
        ville: clientDetails.ville,
        departement: clientDetails.dpt,
        technicien: rdv?.intervenant || clientDetails.technicien,
        telepro: clientDetails.telepro,
        type_rdv: rdv?.type,
        date_rdv: rdv?.date,
        heure_rdv: rdv?.heure,
        etats_generaux: clientDetails.etats_generaux,
        source: clientDetails.source
      };
      
      const resp = await authFetch(`${API}/api/ia/question-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: iaQuestion,
          client_context: clientContext,
          ia_responsable: iaResponsable.name,
          rdv_type: rdv?.type
        })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setIaResponse(data);
        setIaQuestion(""); // Clear input après envoi
      } else {
        setIaResponse({
          reponse: "Désolé, je n'ai pas pu traiter votre question. Veuillez réessayer.",
          error: true
        });
      }
    } catch (e) {
      console.error("Erreur question IA:", e);
      setIaResponse({
        reponse: "Erreur de connexion avec l'assistant IA.",
        error: true
      });
    }
    setIaLoading(false);
  };

  // Formater la durée
  const formatDuration = (mins) => {
    if (!mins) return "1h30";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h${m.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`p-4 border-b border-slate-700 flex items-center justify-between ${rdv?.type === "R1" ? "bg-blue-500/20" : "bg-purple-500/20"}`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${rdv?.type === "R1" ? "bg-blue-500" : "bg-purple-500"}`}>
              {rdv?.type}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{rdv?.client || "Client"}</h2>
              <p className="text-sm text-slate-400">
                {rdv?.date?.split('-').reverse().join('/')} à {rdv?.heure} • Durée: {formatDuration(rdv?.duration)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Infos client - Mode affichage ou édition */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-400" />
                    Informations Client
                    {!hasCompleteInfo() && (
                      <span className="bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Incomplet
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => setEditingInfo(!editingInfo)}
                    className={`px-3 py-1 rounded text-xs flex items-center gap-1 transition-colors ${
                      editingInfo 
                        ? "bg-slate-600 text-white" 
                        : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
                    }`}
                  >
                    <Edit3 className="w-3 h-3" />
                    {editingInfo ? "Annuler" : "Modifier"}
                  </button>
                </div>
                
                {!editingInfo ? (
                  /* Mode affichage */
                  <div className="grid grid-cols-2 gap-4">
                    {/* Contact */}
                    <div className="space-y-2 text-sm">
                      <p className="text-white font-medium">
                        {clientDetails?.nom || rdv?.client} {clientDetails?.prenom || ""}
                      </p>
                      {(clientDetails?.telephones?.length > 0 || rdv?.telephone) ? (
                        <p className="text-slate-300 flex items-center gap-2">
                          <Phone className="w-3 h-3 text-green-400" />
                          {clientDetails?.telephones?.[0] || rdv?.telephone}
                        </p>
                      ) : (
                        <p className="text-amber-400/70 flex items-center gap-2 text-xs">
                          <Phone className="w-3 h-3" />
                          Téléphone manquant
                        </p>
                      )}
                      {clientDetails?.emails?.length > 0 && (
                        <p className="text-slate-400 flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {clientDetails.emails[0]}
                        </p>
                      )}
                    </div>
                    
                    {/* Adresse */}
                    <div className="space-y-1 text-sm">
                      {clientDetails?.adresse ? (
                        <>
                          <p className="text-slate-300 flex items-center gap-2">
                            <MapPin className="w-3 h-3 text-green-400" />
                            {clientDetails.adresse}
                          </p>
                          <p className="text-slate-400 pl-5">
                            {clientDetails?.code_postal || clientDetails?.cp || ""} {clientDetails?.ville || ""}
                          </p>
                        </>
                      ) : (
                        <p className="text-amber-400/70 flex items-center gap-2 text-xs">
                          <MapPin className="w-3 h-3" />
                          Adresse manquante
                        </p>
                      )}
                      <p className="text-slate-500 text-xs pl-5">Dép: {clientDetails?.dpt || rdv?.departement || "?"}</p>
                    </div>
                  </div>
                ) : (
                  /* Mode édition */
                  <div className="space-y-3">
                    {/* Téléphone */}
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Téléphone *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="tel"
                          value={editForm.telephone}
                          onChange={e => setEditForm(prev => ({ ...prev, telephone: e.target.value }))}
                          className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                          placeholder="06 12 34 56 78"
                        />
                      </div>
                    </div>
                    
                    {/* Adresse avec autocomplétion */}
                    <div className="relative">
                      <label className="block text-xs text-slate-400 mb-1">Adresse * (commencez à taper)</label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <input
                          type="text"
                          value={editForm.adresse}
                          onChange={e => {
                            setEditForm(prev => ({ ...prev, adresse: e.target.value }));
                            searchAddress(e.target.value);
                          }}
                          onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                          className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                          placeholder="Ex: 12 rue de la Paix"
                        />
                        {loadingAddress && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
                          </div>
                        )}
                      </div>
                      
                      {/* Suggestions d'adresses */}
                      {showSuggestions && addressSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                          {addressSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectAddress(suggestion)}
                              className="w-full px-3 py-2 text-left hover:bg-slate-700 border-b border-slate-700 last:border-b-0 transition-colors"
                            >
                              <p className="text-sm text-white">{suggestion.full || suggestion.adresse}</p>
                              <p className="text-xs text-slate-400">{suggestion.cp} {suggestion.ville}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* CP et Ville */}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Code Postal</label>
                        <input
                          type="text"
                          value={editForm.cp}
                          onChange={e => setEditForm(prev => ({ ...prev, cp: e.target.value }))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                          placeholder="75001"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Ville *</label>
                        <input
                          type="text"
                          value={editForm.ville}
                          onChange={e => setEditForm(prev => ({ ...prev, ville: e.target.value }))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-blue-500 focus:outline-none"
                          placeholder="Paris"
                        />
                      </div>
                    </div>
                    
                    {/* Bouton sauvegarder */}
                    <div className="flex justify-end pt-2">
                      <button
                        onClick={saveClientInfo}
                        disabled={savingInfo}
                        className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-700 rounded-lg text-white text-sm flex items-center gap-2"
                      >
                        {savingInfo ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4" />
                        )}
                        Enregistrer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Intervenants */}
              <div className="grid grid-cols-3 gap-4">
                {/* Technicien / Architecte */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">
                    {rdv?.type === "R1" ? "Technicien" : "Architecte"}
                  </p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-amber-400" />
                    {rdv?.intervenant || clientDetails?.technicien || "Non assigné"}
                  </p>
                </div>

                {/* Télépro */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Télépro en charge</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    {clientDetails?.telepro || "Non assigné"}
                  </p>
                </div>

                {/* Source */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">Source</p>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Tag className="w-4 h-4 text-purple-400" />
                    {clientDetails?.source || "Non définie"}
                  </p>
                </div>
              </div>

              {/* Call Center / Régie */}
              {(clientDetails?.call_center || clientDetails?.regie_commerciale) && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                  <p className="text-xs text-amber-400/70 mb-1">Origine du lead</p>
                  <p className="text-amber-400 font-medium flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    {clientDetails?.call_center && `Call Center: ${clientDetails.call_center}`}
                    {clientDetails?.regie_commerciale && `Régie: ${clientDetails.regie_commerciale}`}
                  </p>
                </div>
              )}

              {/* Commentaire RDV */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-400" />
                    Commentaire du RDV
                  </h3>
                  {!editingComment ? (
                    <button
                      onClick={() => setEditingComment(true)}
                      className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-slate-300 flex items-center gap-1"
                    >
                      <Edit3 className="w-3 h-3" />
                      Modifier
                    </button>
                  ) : (
                    <button
                      onClick={saveCommentaire}
                      disabled={saving}
                      className="px-2 py-1 bg-green-600 hover:bg-green-500 rounded text-xs text-white flex items-center gap-1 disabled:opacity-50"
                    >
                      {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                      Enregistrer
                    </button>
                  )}
                </div>
                
                {editingComment ? (
                  <textarea
                    value={commentaire}
                    onChange={(e) => setCommentaire(e.target.value)}
                    placeholder="Ajouter un commentaire pour ce RDV..."
                    className="w-full p-3 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm resize-none"
                    rows={4}
                    autoFocus
                  />
                ) : (
                  <div className="min-h-[60px]">
                    {commentaire ? (
                      <p className="text-slate-300 text-sm whitespace-pre-wrap">{commentaire}</p>
                    ) : (
                      <p className="text-slate-500 text-sm italic">Aucun commentaire pour ce RDV</p>
                    )}
                  </div>
                )}
              </div>

              {/* Infos supplémentaires */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-800/30 rounded-lg p-3">
                  <p className="text-slate-500 mb-1">États généraux</p>
                  <p className="text-slate-300">{clientDetails?.etats_generaux || "Non défini"}</p>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-3">
                  <p className="text-slate-500 mb-1">Date de prise de RDV</p>
                  <p className="text-slate-300">{clientDetails?.date_prise_rdv || "Non définie"}</p>
                </div>
              </div>
              
              {/* Section Question IA */}
              <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                    iaResponsable.color === "blue" ? "bg-blue-500/20" :
                    iaResponsable.color === "purple" ? "bg-purple-500/20" : "bg-amber-500/20"
                  }`}>
                    {iaResponsable.avatar}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      Question à {iaResponsable.name}
                    </h3>
                    <p className="text-xs text-slate-400">{iaResponsable.role}</p>
                  </div>
                </div>
                
                {/* Réponse IA */}
                {iaResponse && (
                  <div className={`mb-3 p-3 rounded-lg ${
                    iaResponse.error ? "bg-red-500/10 border border-red-500/30" : "bg-amber-500/10 border border-amber-500/20"
                  }`}>
                    <div className="flex items-start gap-2">
                      <Bot className={`w-4 h-4 mt-0.5 ${iaResponse.error ? "text-red-400" : "text-amber-400"}`} />
                      <p className={`text-sm ${iaResponse.error ? "text-red-300" : "text-slate-300"}`}>
                        {iaResponse.reponse}
                      </p>
                    </div>
                    {iaResponse.suggestions && (
                      <div className="mt-2 pl-6">
                        <p className="text-xs text-slate-500 mb-1">Suggestions :</p>
                        <ul className="text-xs text-slate-400 list-disc list-inside">
                          {iaResponse.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Input question */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={iaQuestion}
                    onChange={(e) => setIaQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && askIaQuestion()}
                    placeholder={`Ex: Quel est l'historique de ce client ? Comment optimiser ce RDV ?`}
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-amber-500/50 focus:outline-none"
                    disabled={iaLoading}
                  />
                  <button
                    onClick={askIaQuestion}
                    disabled={iaLoading || !iaQuestion.trim()}
                    className="px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-white flex items-center gap-2 transition-colors"
                  >
                    {iaLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (onViewFullProfile && rdv?.rowNumber) {
                  console.log("Navigating to full profile for row:", rdv.rowNumber);
                  onViewFullProfile(rdv.rowNumber);
                  onClose(); // Fermer le modal après avoir initié la navigation
                }
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white flex items-center gap-2 transition-colors"
              data-testid="view-full-profile-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Voir la fiche complète
            </button>
            
            {/* Bouton Supprimer RDV */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600/20 hover:bg-red-600/40 border border-red-500/50 rounded-lg text-red-400 flex items-center gap-2 transition-colors"
              data-testid="delete-rdv-btn"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </button>
          </div>
          
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white"
          >
            Fermer
          </button>
        </div>
        
        {/* Modal de confirmation de suppression avec raison */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl">
            <div className="bg-slate-800 border border-red-500/50 rounded-xl p-6 max-w-lg mx-4 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Supprimer ce RDV ?</h3>
                  <p className="text-sm text-slate-400">Une trace sera conservée dans l'historique</p>
                </div>
              </div>
              
              <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-300">
                  <span className="text-slate-500">Client :</span> {rdv?.client || clientDetails?.nom + ' ' + clientDetails?.prenom}
                </p>
                <p className="text-sm text-slate-300">
                  <span className="text-slate-500">Date :</span> {rdv?.date}
                </p>
                <p className="text-sm text-slate-300">
                  <span className="text-slate-500">Type :</span> {rdv?.type || 'R1'}
                </p>
              </div>
              
              {/* Sélection de la raison */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Raison de la suppression <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2.5 text-white appearance-none cursor-pointer focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    data-testid="delete-reason-select"
                  >
                    <option value="">-- Sélectionner une raison --</option>
                    {RAISONS_ANNULATION.map((raison) => (
                      <option key={raison.id} value={raison.id}>
                        {raison.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                </div>
              </div>
              
              {/* Commentaire optionnel */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Commentaire (optionnel)
                </label>
                <textarea
                  value={deleteComment}
                  onChange={(e) => setDeleteComment(e.target.value)}
                  placeholder="Détails supplémentaires..."
                  rows={2}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none"
                  data-testid="delete-comment-input"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteReason("");
                    setDeleteComment("");
                  }}
                  disabled={deleting}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeleteRdv}
                  disabled={deleting || !deleteReason}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-white flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirm-delete-rdv-btn"
                >
                  {deleting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deleting ? 'Suppression...' : 'Confirmer la suppression'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
